import React, { Suspense, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorksheetDashboardPage from './worksheet/WorksheetDashboardPage';
import GenieclassDashboardPage from './online_lesson/GenieclassDashboardPage';
import { Helmet } from 'react-helmet-async';
import ProgressDashboardPage from '@pages/progress/ProgressDashboardPage';
import { useQuery } from '@tanstack/react-query';
import { Button, PopUp, LoadingScroll } from 'geniebook-ui/dist';
import IllustGCFinished from '@assets/images/Illust_GC_Finished Quiz.svg';
import {
  subjectIdFromName,
  subjectRouteFromId,
} from '@utils/helpers/SubjectParser';
import {
  IClassToSmartEvent,
  IPostClassEvent,
} from '@components/navigation/notification/GetNotificationAction';
import {
  IGetQuizQuestionResponse,
  IWorksheetCardProp,
} from '@models/interfaces/worksheet.interface';
import { levelName } from '@utils/helpers/levelName';
import GenieClassServices from '@services/GeniebookAPI/GenieClassServices.services';
import Class2SmartPopupHandler from '@components/student/class2smart/Class2SmartPopupHandler';
import { subscriptionsAtom } from '@components/subscription/store/SubscriptionAtoms';
import { useAtom } from 'jotai';
import { showGenieAskFloatingButtonAtom } from '@atom/worksheetAtom';
import { useIsJCUser, useSubscriptionData } from '@hooks/user.hook';
import GCLASS_SERVICE_API from '@services/microservices/class.service';
import { IGetCheckQuizTimeResponse } from '@models/interfaces/online_lesson.interface';

interface IEventDataRef {
  online_class_id?: number;
  subject_id?: number;
  quiz_type?: string;
  class_name?: string;
  enabled_fetch_question?: boolean;
}

export default function DashboardPage() {
  const { menuId, subjectId: subjectName } = useParams();
  const subjectId = subjectIdFromName(subjectName?.trim());
  const { isJC } = useIsJCUser({
    subjectId: Number(subjectIdFromName(subjectName)),
  });
  const subscriptionsQuery = useSubscriptionData();
  const [studentSubscription, setStudentSubscription] =
    useAtom(subscriptionsAtom);
  const [, setShowGenieAskFloatingButton] = useAtom(
    showGenieAskFloatingButtonAtom,
  );
  const subjectSubscription = studentSubscription.find(
    item => item.subject_id.toString() === subjectId.toString(),
  );

  //! until `subscription/subject_subscription` endpoint updated, `trial` plan should check `has_genieask` instead
  if (subjectSubscription) {
    if (subjectSubscription.is_trial_plan) {
      setShowGenieAskFloatingButton(subjectSubscription.has_genieask === '1');
    } else {
      setShowGenieAskFloatingButton(
        subjectSubscription.is_has_genieask === true,
      );
    }
  }

  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);

  // GenieClass popup
  const [showGenieClassPopup, setShowGenieClassPopup] = React.useState(false);
  const eventDataRef = React.useRef<IEventDataRef>({});
  const [quizFetchData, setQuizFetchData] =
    React.useState<IGetQuizQuestionResponse>(); // for quiz fetch data
  const navigate = useNavigate();

  // C2S state
  const [isClassToSmart, setIsClassToSmart] = useState(false);
  const [class2smartProp, setClass2smartProp] = useState<
    IWorksheetCardProp & { isOpen: boolean }
  >({
    isOpen: false,
    worksheet_id: '',
    topic: '',
    is_recommended_worksheet: 0,
    lesson_plan_topics: [],
    lesson_plan_knowledges: [],
    teacher_id: '',
    worksheet_name: '',
    time_limit: '',
    online_lesson_id: '',
    worksheet_type: '',
    completed: '',
  });

  const handleCloseGenieClassPopup = () => {
    setShowGenieClassPopup(false);
  };

  const useQuizQuestionList = () => {
    return useQuery(
      ['questionList'],
      async () => {
        const response = await GCLASS_SERVICE_API.get<IGetQuizQuestionResponse>(
          `online-class/${eventDataRef.current.online_class_id}/${eventDataRef.current.subject_id}/quiz/${eventDataRef.current.quiz_type}`,
        );

        return response.data;
      },
      {
        enabled:
          !!eventDataRef.current.online_class_id &&
          eventDataRef.current.enabled_fetch_question,
      },
    );
  };

  const useCheckQuizTime = () => {
    return useQuery(
      ['checkQuizTime'],
      async () => {
        if (eventDataRef.current.online_class_id) {
          const response =
            await GCLASS_SERVICE_API.get<IGetCheckQuizTimeResponse>(
              `student/online-class/check-quiz-time`,
              {
                params: {
                  online_class_id: eventDataRef.current.online_class_id,
                  quiz_type: eventDataRef.current.quiz_type,
                },
              },
            );

          return response.data;
        }
      },
      {
        enabled: !!eventDataRef.current.online_class_id,
      },
    );
  };
  const getQuizQuestionList = useQuizQuestionList();
  const checkQuizTime = useCheckQuizTime();

  useEffect(() => {
    const handlePostClassEvent = (event: CustomEventInit<IPostClassEvent>) => {
      eventDataRef.current = {
        online_class_id: event.detail?.onlineLessonId,
        subject_id: event.detail?.subjectId,
        quiz_type: event.detail?.quizType.toString(),
        class_name: event.detail?.className,
        enabled_fetch_question: false,
      };

      setShowLoadingScreen(true);
      setIsClassToSmart(false);

      checkQuizTime.remove();
      checkQuizTime.refetch().then(response => {
        if (response.data) {
          const isClosed = response.data.finished || response.data.closed;
          if (!isClosed) {
            eventDataRef.current.enabled_fetch_question = true;
            getQuizQuestionList.remove();
            getQuizQuestionList.refetch();
          } else {
            setShowLoadingScreen(false);
            setShowGenieClassPopup(true);
          }
        }
      });
    };

    window.addEventListener('postClassEvent', handlePostClassEvent);

    return () => {
      window.removeEventListener('postClassEvent', handlePostClassEvent);
    };
  }, []);

  // class to smart popup handler
  useEffect(() => {
    const handleClassToSmartEvent = (
      event: CustomEventInit<IClassToSmartEvent>,
    ) => {
      setShowLoadingScreen(true);
      if (!event.detail?.subjectId || !event.detail?.onlineLessonId) {
        setShowLoadingScreen(false);
        return;
      }

      const subjectId = Number(event.detail?.subjectId) || 0;
      setIsClassToSmart(true);

      GenieClassServices.getLessonDetail(
        subjectId,
        event.detail.onlineLessonId,
        response => {
          if (response.data !== undefined) {
            const lessonData = response.data;
            const lessonName = `${levelName(
              parseInt(String(lessonData.level_id)),
            )} ${lessonData.topic.trim()}`;

            const lessonPlanTopics =
              lessonData.c2s_details?.lesson_plan_topics ?? [];
            const lessonPlanKnowledges =
              lessonData.c2s_details?.lesson_plan_knowledges ?? [];

            setShowLoadingScreen(false);
            if (lessonData.c2s_details?.c2s_worksheet_id) {
              navigate(
                `/${subjectRouteFromId(
                  subjectId,
                )}/worksheets/worksheet_question/${
                  lessonData.c2s_details.c2s_worksheet_id
                }`,
              );
            } else {
              setClass2smartProp(props => {
                return {
                  ...props,
                  worksheet_id: lessonData.c2s_details
                    ? lessonData.c2s_details.c2s_worksheet_id.toString()
                    : '',
                  isOpen: true,
                  topic: lessonName,
                  worksheet_name: lessonName,
                  lesson_plan_topics: lessonPlanTopics,
                  lesson_plan_knowledges: lessonPlanKnowledges,
                  time_limit: lessonData.c2s_details?.c2s_time_limit ?? '',
                  online_lesson_id: lessonData.online_class_id.toString(),
                };
              });
            }
          }
        },
      );
    };

    window.addEventListener('classToSmartEvent', handleClassToSmartEvent);

    return () => {
      window.removeEventListener('classToSmartEvent', handleClassToSmartEvent);
    };
  });

  useEffect(() => {
    if (
      !getQuizQuestionList.isFetching &&
      getQuizQuestionList.isFetched &&
      showLoadingScreen &&
      !isClassToSmart
    ) {
      setShowLoadingScreen(false);
      setShowGenieClassPopup(true);
    }
    if (getQuizQuestionList.data) {
      setQuizFetchData(getQuizQuestionList.data);
    }
  }, [getQuizQuestionList, isClassToSmart]);

  useEffect(() => {
    if (subscriptionsQuery.data?.data?.data) {
      setStudentSubscription(subscriptionsQuery.data.data.data);
    }
  }, [subscriptionsQuery.data?.data?.data]);

  return (
    <>
      {(() => {
        switch (menuId) {
          //Geniesmart page
          case 'worksheets':
            return (
              <>
                <WorksheetDashboardPage />
              </>
            );
          //Genieclass page
          case 'classes':
            return (
              <>
                <Suspense>
                  <GenieclassDashboardPage />
                </Suspense>
              </>
            );
          case 'progress':
            return (
              <>
                <Suspense>
                  <ProgressDashboardPage />
                </Suspense>
              </>
            );
          default:
            return (
              <>
                <Helmet>
                  <title>Geniebook</title>
                  <meta name="description" content="Geniebook" />
                </Helmet>
                <div>no router</div>
              </>
            );
        }
      })()}
      {/* Popups */}
      {showLoadingScreen && <LoadingScroll isBlockOthers={true} />}
      {showGenieClassPopup && checkQuizTime.data && (
        <PopUp
          {...(isJC === false && {
            img: (
              <IllustGCFinished
                width={260}
                height={260}
                className="mx-auto pb-4"
              />
            ),
          })}
          buttonGroup={
            <div className="flex flex-col modal-footer mt-5">
              <Button
                variant="primary"
                onClick={() => {
                  if (
                    checkQuizTime.data?.finished ||
                    checkQuizTime.data?.closed
                  )
                    handleCloseGenieClassPopup();
                  else {
                    navigate(
                      `/${subjectRouteFromId(
                        Number(eventDataRef.current.subject_id) ?? 0,
                      )}/quiz/${eventDataRef.current.quiz_type}/${
                        eventDataRef.current.online_class_id
                      }`,
                    );
                  }
                }}
              >
                {!checkQuizTime.data.closed &&
                !checkQuizTime.data.finished &&
                quizFetchData?.data
                  ? "Let's go"
                  : 'Got it'}
              </Button>
              {quizFetchData?.data &&
                !checkQuizTime.data.closed &&
                !checkQuizTime.data.finished && (
                  <Button
                    variant="secondary"
                    onClick={handleCloseGenieClassPopup}
                  >
                    Dismiss
                  </Button>
                )}
            </div>
          }
          onClose={() => {}}
          width={'400px'}
          title={
            checkQuizTime.data && !checkQuizTime.isError
              ? checkQuizTime.data.finished
                ? 'Great Effort!'
                : checkQuizTime.data.closed
                ? 'Quiz Ended'
                : 'Quiz Time'
              : 'Oops'
          }
          description={
            checkQuizTime.data && !checkQuizTime.isError
              ? checkQuizTime.data.finished
                ? `You've completed the Quiz for ${
                    eventDataRef.current.class_name ?? 'this class'
                  }.`
                : checkQuizTime.data.closed
                ? 'Pre-quiz expires 10 minutes before your live class starts.'
                : `The GenieClass ${
                    eventDataRef.current.class_name ?? 'Lesson'
                  } Quiz contains ${quizFetchData?.data
                    ?.length} MCQ questions and should take about 15 minutes to complete. Are you ready?`
              : (checkQuizTime.error as string)
          }
          disableBodyScroll={true}
        />
      )}
      <Class2SmartPopupHandler
        show={class2smartProp.isOpen}
        data={class2smartProp}
        onClose={() => {
          setClass2smartProp(props => {
            return {
              ...props,
              isOpen: false,
            };
          });
        }}
      />
    </>
  );
}
